import type { PadusNativeObjectPin, PadusNativeSourceClosure } from '../../types/padusNative';
import type { RefdataFeature, RefdataManifest } from '../../types/refdata';
import { classifyPadusFeature } from '../dataProviders/padusNativeQualification';

export const PADUS_NATIVE_BINDING = '62bdd1de5d5058f948f85cad03bc306513842959045233c5a64bf85a6557990e';
export const PADUS_NATIVE_VERSION = '2026-09-08-native-qualified-v1';
export const PADUS_NATIVE_SOURCE_URL = 'https://services.arcgis.com/v01gqwM5QqNysAAi/arcgis/rest/services/PADUS_Protected_Areas_National/FeatureServer/0';
export const PADUS_NATIVE_SOURCE_ATTRIBUTION = 'PAD-US public ArcGIS inventory; publisher unspecified in retained layer metadata';
export const PADUS_NATIVE_RAW_RECEIPT_SHA256 = '6472ee03267df8459dffdab55e699c2a74229419ffe4f2d912b4560b33558daa';
export const PADUS_NATIVE_GEOMETRY_AUDIT_SHA256 = '8eb6ded9016a9aafe014791f7a766e1175bcade0b2d6596c9ff43dae2d375fd6';
export const PADUS_NATIVE_GEOMETRY_POLICY = 'original-source-vertices-exact-cell-partition-and-explicit-unavailable-unit-envelopes';

const HASH = /^[a-f0-9]{64}$/;
const CELL = /^([ns])(\d{4})([ew])(\d{5})_p50(?:__s\d{4})?$/;

function pin(value: unknown): value is PadusNativeObjectPin {
  if (!value || typeof value !== 'object') return false;
  const p = value as PadusNativeObjectPin;
  return typeof p.path === 'string' && /^refdata\/padus\/[a-zA-Z0-9._/-]+$/.test(p.path)
    && !p.path.split('/').some(part => part === '..' || part === '.' || !part)
    && typeof p.generation === 'string' && /^[1-9]\d*$/.test(p.generation)
    && typeof p.sha256 === 'string' && HASH.test(p.sha256) && Number.isSafeInteger(p.bytes) && p.bytes > 0;
}

export function isPadusNativeSourceClosure(value: unknown, version: string): value is PadusNativeSourceClosure {
  if (!value || typeof value !== 'object') return false;
  const s = value as PadusNativeSourceClosure;
  if (version !== PADUS_NATIVE_VERSION || s.schemaVersion !== 1 || s.version !== version || s.sourceBinding !== PADUS_NATIVE_BINDING
    || s.sourceEdition !== null || s.sourceCount !== 306082 || s.qualifiedInventoryUnits !== 302485 || s.unavailableInventoryUnits !== 3594
    || s.retainedComplementRecords !== 3 || JSON.stringify(s.missingComplementGeometryOids) !== '[261401]'
    || s.negativeFindingQualified !== false || s.geometryPolicy !== PADUS_NATIVE_GEOMETRY_POLICY
    || !pin(s.rawCloudReceipt) || s.rawCloudReceipt.path !== `refdata/padus/2026-09-08-native-source-archive/raw/padus/${PADUS_NATIVE_BINDING}/cloud-receipt.json`
    || s.rawCloudReceipt.sha256 !== PADUS_NATIVE_RAW_RECEIPT_SHA256 || s.rawCloudReceipt.bytes !== 262737 || s.rawCloudReceipt.generation !== '1788863757112881'
    || !pin(s.nativeGeometryAudit) || s.nativeGeometryAudit.sha256 !== PADUS_NATIVE_GEOMETRY_AUDIT_SHA256 || s.nativeGeometryAudit.bytes !== 814
    || !pin(s.nativeAuditRetentionReceipt) || !pin(s.compilerReceipt)
    || ![s.nativeGeometryAudit, s.nativeAuditRetentionReceipt, s.compilerReceipt].every(p => p.path.startsWith(`refdata/padus/${version}/audit/`))
    || !s.cellFiles || typeof s.cellFiles !== 'object' || Array.isArray(s.cellFiles)) return false;
  const files = Object.entries(s.cellFiles);
  return files.length > 0 && files.length <= 100000 && files.every(([id, file]) => {
    const match = CELL.exec(id);
    return !!match && Number(match[2]) <= 9000 && Number(match[4]) <= 18000 && Number(match[2]) % 50 === 0 && Number(match[4]) % 50 === 0
      && file && file.parentCell === id.replace(/__s\d{4}$/, '') && HASH.test(file.sha256)
      && Number.isSafeInteger(file.jsonBytes) && file.jsonBytes > 0 && file.jsonBytes <= 32 * 1024**2
      && Number.isSafeInteger(file.featureCount) && file.featureCount > 0;
  });
}

/** Existing legacy snapshots stay explicitly unqualified for absence. */
export function validPadusNativeManifest(manifest: Partial<RefdataManifest>): boolean {
  if (manifest.datasetId !== 'padus') return manifest.padusNativeSources === undefined;
  if (manifest.padusNativeSources === undefined) return !manifest.version?.startsWith('2026-09-08-native-qualified');
  if (typeof manifest.version !== 'string' || !isPadusNativeSourceClosure(manifest.padusNativeSources, manifest.version)
    || manifest.kind !== 'vector' || manifest.geometryStorage !== 'cell-clipped' || manifest.cellSizeDeg !== .5
    || manifest.sourceUrl !== PADUS_NATIVE_SOURCE_URL || manifest.vintageDate !== 'unknown' || manifest.featureCount !== 306079) return false;
  const files = manifest.padusNativeSources.cellFiles;
  const expectedShards = new Set(Object.values(manifest.cellShards ?? {}).flatMap(rows => rows.map(row => row.id)));
  const actualShards = Object.keys(files).filter(id => /__s\d{4}$/.test(id));
  if (expectedShards.size !== actualShards.length || actualShards.some(id => !expectedShards.has(id))) return false;
  for (const [parent, rows] of Object.entries(manifest.cellShards ?? {})) {
    if (files[parent]) return false;
    for (const row of rows) {
      const file = files[row.id];
      if (!file || file.parentCell !== parent || file.sha256 !== row.sha256 || file.jsonBytes !== row.jsonBytes || file.featureCount !== row.featureCount) return false;
    }
  }
  return true;
}

export function isPadusNativeFeature(feature: RefdataFeature, physicalCell: string): boolean {
  const props = feature?.props;
  if (!props || !['Polygon', 'MultiPolygon'].includes(feature.geom?.type)
    || Object.values(props).some(v => v !== null && (typeof v !== 'string' && typeof v !== 'number' || typeof v === 'number' && !Number.isFinite(v)))
    || ![ 'observed-inventory-unit', 'unavailable-source-envelope' ].includes(String(props.padusContextKind))
    || props.negativeFindingQualified !== 'false' || !Number.isSafeInteger(props.sourcePartIndex) || Number(props.sourcePartIndex) < 0
    || props.sourceFeatureId !== `padus:${PADUS_NATIVE_BINDING}:${props.OBJECTID}`
    || props.sourcePartId !== `${props.sourceFeatureId}:part:${props.sourcePartIndex}`
    || !feature.id.startsWith(`${props.sourcePartId}:cell:${physicalCell.replace(/__s\d{4}$/, '')}:fragment:`)) return false;
  const assessment = classifyPadusFeature(feature);
  return assessment.kind === 'observed' || (assessment.kind === 'unavailable' && !!assessment.unit.sourceQualificationReceiptHash);
}
